import React, { useState, useRef, useEffect, useCallback } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { ReactComponent as PlayRecording } from "../../assets/icons/svg/play-recording.svg";
import { ReactComponent as PauseRecording } from "../../assets/icons/svg/puse-recording.svg";
import { ReactComponent as DeleteRecording } from "../../assets/icons/svg/Delete-recording.svg";
import { BASE_URL } from "../../api/urls";
import { DEFAULT_FONT_FAMILY } from "../../config/fontConfig";

interface AudioPlayerProps {
  feedbackId: string;
  onError?: (error: string) => void;
  onLoadingChange?: (loading: boolean) => void;
  onDelete?: () => void;
  className?: string;
  compact?: boolean;
}

interface AudioPlayerState {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;
  isLoading: boolean;
  error: string | null;
  audioUrl: string | null;
  progress: number;
  isDeleting: boolean;
  isDragging: boolean;
}

// Token Manager
class TokenManager {
  private static ACCESS_TOKEN_KEY = "access_token";

  static getAccessToken(): string | null {
    try {
      return (
        localStorage.getItem(this.ACCESS_TOKEN_KEY) ||
        (window as any).__feebdack_tokens?.access_token ||
        null
      );
    } catch (error) {
      console.warn("Could not retrieve access token:", error);
      return (window as any).__feebdack_tokens?.access_token || null;
    }
  }
}

// API URLs
const API_URLS = {
  GET_FILE_VOICE: (feedbackId: string | number) =>
    `${BASE_URL}/feedback/get/voice?feedback_id=${feedbackId}`,
  DELETE_FILE_VOICE: (feedbackId: string | number) =>
    `${BASE_URL}/feedback/delete/voice?feedback_id=${feedbackId}`,
  DELETE_FILE_VOICE_ALT: (feedbackId: string | number) =>
    `${BASE_URL}/feedback/voice/${feedbackId}`,
};

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  feedbackId,
  onError,
  onLoadingChange,
  onDelete,
  className = "",
  compact = false,
}) => {
  const [state, setState] = useState<AudioPlayerState>({
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    volume: 1,
    isMuted: false,
    isLoading: false,
    error: null,
    audioUrl: null,
    progress: 0,
    isDeleting: false,
    isDragging: false,
  });

  const [showVolumeSlider, setShowVolumeSlider] = useState(false);

  const audioRef = useRef<HTMLAudioElement>(null);
  const loadingAbortControllerRef = useRef<AbortController | null>(null);
  const deleteAbortControllerRef = useRef<AbortController | null>(null);
  const volumeRef = useRef<HTMLDivElement>(null);
  const volumeSliderRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);

  // Get responsive dimensions and classes
  const getResponsiveClasses = () => {
    if (compact) {
      return {
        containerHeight: "h-8",
        iconSize: "w-6 h-6",
        buttonSize: "w-7 h-7",
        buttonGap: "gap-2",
      };
    }

    const screenWidth = window.innerWidth;

    let containerHeight = "h-10";
    let iconSize = "w-5 h-5";
    let buttonSize = "w-10 h-10";
    let buttonGap = "gap-3";

    if (screenWidth >= 2560) {
      containerHeight = "h-16";
      iconSize = "w-8 h-8";
      buttonSize = "w-16 h-16";
      buttonGap = "gap-4";
    } else if (screenWidth >= 1920) {
      containerHeight = "h-14";
      iconSize = "w-6 h-6";
      buttonSize = "w-14 h-14";
      buttonGap = "gap-4";
    } else if (screenWidth >= 1600) {
      containerHeight = "h-12";
      iconSize = "w-6 h-6";
      buttonSize = "w-12 h-12";
      buttonGap = "gap-3";
    } else if (screenWidth >= 1440) {
      containerHeight = "h-12";
      iconSize = "w-6 h-6";
      buttonSize = "w-12 h-12";
      buttonGap = "gap-3";
    }

    return {
      containerHeight,
      iconSize,
      buttonSize,
      buttonGap,
    };
  };

  const responsiveClasses = getResponsiveClasses();

  // Optimized audio loading function
  const loadAudio = useCallback(async () => {
    if (loadingAbortControllerRef.current) {
      loadingAbortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    loadingAbortControllerRef.current = abortController;

    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    onLoadingChange?.(true);

    try {
      const accessToken = TokenManager.getAccessToken();
      if (!accessToken) {
        throw new Error("No access token available");
      }

      const apiUrl = API_URLS.GET_FILE_VOICE(feedbackId);
      console.log("🎵 Loading audio from API:", apiUrl);

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        signal: abortController.signal,
      });

      if (abortController.signal.aborted) {
        return;
      }

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("Audio not found for this feedback");
        }
        throw new Error(
          `Failed to load audio: ${response.status} ${response.statusText}`,
        );
      }

      const audioBlob = await response.blob();

      if (abortController.signal.aborted) {
        return;
      }

      if (audioBlob.size === 0) {
        throw new Error("Audio file is empty");
      }

      if (state.audioUrl) {
        URL.revokeObjectURL(state.audioUrl);
      }

      const audioUrl = URL.createObjectURL(audioBlob);
      setState((prev) => ({
        ...prev,
        audioUrl,
        isLoading: false,
        error: null,
      }));
      onLoadingChange?.(false);

      console.log("✅ Audio loaded successfully:", {
        feedbackId,
        blobSize: audioBlob.size,
        blobType: audioBlob.type,
      });
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        console.log("Audio loading aborted");
        return;
      }

      const errorMessage =
        error instanceof Error ? error.message : "Failed to load audio";
      setState((prev) => ({
        ...prev,
        error: errorMessage,
        isLoading: false,
      }));
      onError?.(errorMessage);
      onLoadingChange?.(false);
      console.error("❌ Failed to load audio:", error);
    }
  }, [feedbackId, onError, onLoadingChange, state.audioUrl]);

  // Enhanced delete audio function
  const deleteAudioFromDatabase = useCallback(async (): Promise<boolean> => {
    if (deleteAbortControllerRef.current) {
      deleteAbortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    deleteAbortControllerRef.current = abortController;

    try {
      const accessToken = TokenManager.getAccessToken();
      if (!accessToken) {
        throw new Error("No access token available");
      }

      console.log("🗑️ Attempting delete strategy 1: DELETE with query param");
      const deleteUrl = API_URLS.DELETE_FILE_VOICE(feedbackId);

      let response = await fetch(deleteUrl, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        signal: abortController.signal,
      });

      if (abortController.signal.aborted) {
        return false;
      }

      if (response.ok) {
        console.log("✅ Audio deleted successfully (strategy 1)");
        return true;
      }

      if (response.status === 404) {
        console.log("⚠️ Audio not found in database (already deleted)");
        return true;
      }

      if (response.status === 422 || response.status === 405) {
        console.log("🗑️ Attempting delete strategy 2: DELETE with REST URL");
        const altDeleteUrl = API_URLS.DELETE_FILE_VOICE_ALT(feedbackId);

        response = await fetch(altDeleteUrl, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          signal: abortController.signal,
        });

        if (response.ok || response.status === 404) {
          console.log("✅ Audio deleted successfully (strategy 2)");
          return true;
        }
      }

      let errorMessage = `Failed to delete audio: ${response.status} ${response.statusText}`;
      throw new Error(errorMessage);
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        console.log("Audio deletion aborted");
        return false;
      }

      console.error("❌ Failed to delete audio from database:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to delete audio";
      onError?.(errorMessage);
      return false;
    }
  }, [feedbackId, onError]);

  // Load audio when feedbackId changes
  useEffect(() => {
    if (feedbackId) {
      loadAudio();
    }

    return () => {
      if (loadingAbortControllerRef.current) {
        loadingAbortControllerRef.current.abort();
      }
      if (deleteAbortControllerRef.current) {
        deleteAbortControllerRef.current.abort();
      }
      if (state.audioUrl) {
        URL.revokeObjectURL(state.audioUrl);
      }
    };
  }, [feedbackId]);

  // Apply volume changes to audio element
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = state.volume;
      audioRef.current.muted = state.isMuted;
    }
  }, [state.volume, state.isMuted]);

  // Audio event handlers
  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      setState((prev) => ({
        ...prev,
        duration: audioRef.current!.duration,
        currentTime: audioRef.current!.currentTime,
      }));
    }
  }, []);

  // Smooth progress update using requestAnimationFrame
  useEffect(() => {
    if (!audioRef.current || state.isDragging || !state.isPlaying) return;

    let animationFrameId: number | null = null;

    const updateProgress = () => {
      if (audioRef.current && !state.isDragging && state.isPlaying) {
        const currentTime = audioRef.current.currentTime;
        const duration = audioRef.current.duration;
        const progress = duration > 0 ? currentTime / duration : 0;

        setState((prev) => ({
          ...prev,
          currentTime,
          progress,
        }));

        animationFrameId = requestAnimationFrame(updateProgress);
      }
    };

    animationFrameId = requestAnimationFrame(updateProgress);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [state.isDragging, state.isPlaying]);

  const handleTimeUpdate = useCallback(() => {
    // Fallback for paused state
    if (audioRef.current && !state.isDragging && !state.isPlaying) {
      const currentTime = audioRef.current.currentTime;
      const duration = audioRef.current.duration;
      const progress = duration > 0 ? currentTime / duration : 0;

      setState((prev) => ({
        ...prev,
        currentTime,
        progress,
      }));
    }
  }, [state.isDragging, state.isPlaying]);

  const handleEnded = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isPlaying: false,
      currentTime: 0,
      progress: 0,
    }));
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  }, []);

  const handleError = useCallback(() => {
    setState((prev) => ({
      ...prev,
      error: "Audio playback error",
      isPlaying: false,
    }));
  }, []);

  // Control handlers
  const togglePlayPause = useCallback(() => {
    if (!audioRef.current || !state.audioUrl || state.isDeleting) return;

    if (state.isPlaying) {
      audioRef.current.pause();
      setState((prev) => ({ ...prev, isPlaying: false }));
    } else {
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setState((prev) => ({ ...prev, isPlaying: true }));
          })
          .catch((error) => {
            setState((prev) => ({
              ...prev,
              error: `Playback failed: ${error.message}`,
              isPlaying: false,
            }));
          });
      }
    }
  }, [state.isPlaying, state.audioUrl, state.isDeleting]);

  // Progress bar dragging functionality
  const handleProgressBarMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!audioRef.current || !progressBarRef.current || state.isDeleting)
        return;

      e.preventDefault();
      e.stopPropagation();

      const wasPlaying = state.isPlaying;
      if (wasPlaying) {
        audioRef.current.pause();
      }

      setState((prev) => ({ ...prev, isDragging: true }));

      const updateProgress = (clientX: number) => {
        if (!progressBarRef.current || !audioRef.current) return;

        const rect = progressBarRef.current.getBoundingClientRect();
        const x = clientX - rect.left;
        const percentage = Math.max(0, Math.min(1, x / rect.width));
        const newTime = percentage * audioRef.current.duration;

        audioRef.current.currentTime = newTime;
        setState((prev) => ({
          ...prev,
          progress: percentage,
          currentTime: newTime,
        }));
      };

      updateProgress(e.clientX);

      const handleMouseMove = (moveEvent: MouseEvent) => {
        updateProgress(moveEvent.clientX);
      };

      const handleMouseUp = () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";

        setState((prev) => ({ ...prev, isDragging: false }));

        if (wasPlaying && audioRef.current) {
          audioRef.current.play();
        }
      };

      document.body.style.userSelect = "none";
      document.body.style.cursor = "pointer";
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [state.isPlaying, state.isDeleting],
  );

  // Volume controls
  const toggleVolumeSlider = useCallback(() => {
    console.log("Volume slider toggle clicked:", !showVolumeSlider);
    setShowVolumeSlider(!showVolumeSlider);
  }, [showVolumeSlider]);

  const toggleMute = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isMuted: !prev.isMuted,
    }));
  }, []);

  const handleVolumeSliderMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const updateVolume = (clientY: number) => {
      if (!volumeSliderRef.current) return;

      const rect = volumeSliderRef.current.getBoundingClientRect();
      const y = clientY - rect.top;
      const percentage = Math.max(0, Math.min(1, 1 - y / rect.height));

      setState((prev) => ({
        ...prev,
        volume: percentage,
        isMuted: percentage === 0,
      }));
    };

    updateVolume(e.clientY);

    const handleMouseMove = (moveEvent: MouseEvent) => {
      updateVolume(moveEvent.clientY);
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "";
    };

    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, []);

  // Click outside handler for volume slider
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showVolumeSlider &&
        volumeRef.current &&
        !volumeRef.current.contains(event.target as Node)
      ) {
        setShowVolumeSlider(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showVolumeSlider]);

  // Enhanced delete handler
  const handleDelete = useCallback(async () => {
    if (state.isDeleting) return;

    setState((prev) => ({ ...prev, isDeleting: true }));

    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      const deleteSuccess = await deleteAudioFromDatabase();

      if (state.audioUrl) {
        URL.revokeObjectURL(state.audioUrl);
      }

      setState({
        isPlaying: false,
        currentTime: 0,
        duration: 0,
        volume: 1,
        isMuted: false,
        isLoading: false,
        error: null,
        audioUrl: null,
        progress: 0,
        isDeleting: false,
        isDragging: false,
      });

      onDelete?.();

      if (deleteSuccess) {
        console.log("✅ Audio deletion completed successfully");
      } else {
        console.log("⚠️ Server deletion failed, but local state cleared");
      }
    } catch (error) {
      console.error("❌ Error during audio deletion:", error);

      if (state.audioUrl) {
        URL.revokeObjectURL(state.audioUrl);
      }

      setState({
        isPlaying: false,
        currentTime: 0,
        duration: 0,
        volume: 1,
        isMuted: false,
        isLoading: false,
        error: null,
        audioUrl: null,
        progress: 0,
        isDeleting: false,
        isDragging: false,
      });

      onDelete?.();
    }
  }, [state.isDeleting, state.audioUrl, deleteAudioFromDatabase, onDelete]);

  // Error state
  if (state.error && !state.isDeleting) {
    return (
      <div className={`w-full ${className}`}>
        <div className={`flex items-center ${responsiveClasses.buttonGap}`}>
          <div
            className={`flex-1 ${responsiveClasses.containerHeight} bg-red-50 border border-red-200 rounded-[8px] flex items-center px-3 overflow-hidden relative`}
          >
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 text-red-500">⚠️</div>
              <span
                className="text-sm text-red-700"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                {state.error}
              </span>
            </div>
          </div>

          <button
            onClick={handleDelete}
            disabled={state.isDeleting}
            className={`flex items-center justify-center transition-opacity ${
              state.isDeleting
                ? "opacity-50 cursor-not-allowed"
                : "hover:opacity-80"
            }`}
            style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            aria-label="Delete recording"
          >
            <DeleteRecording
              className={`${responsiveClasses.buttonSize} text-red-600`}
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            />
          </button>
        </div>
      </div>
    );
  }

  // Deleting state
  if (state.isDeleting) {
    return (
      <div className={`w-full ${className}`}>
        <div className={`flex items-center ${responsiveClasses.buttonGap}`}>
          <div
            className={`flex-1 ${responsiveClasses.containerHeight} bg-orange-50 border border-orange-200 rounded-[8px] flex items-center px-3 overflow-hidden relative`}
          >
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 animate-spin">
                <svg className="w-full h-full" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
              <span
                className="text-sm text-orange-700"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                Deleting audio...
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main audio player UI
  return (
    <div className={`w-full ${className} relative`}>
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={state.audioUrl || undefined}
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        onError={handleError}
        preload="metadata"
      />

      <div className={`flex items-center ${responsiveClasses.buttonGap}`}>
        <div
          className={`flex-1 ${responsiveClasses.containerHeight} bg-[#F4F5F6] rounded-[8px] flex items-center px-3 relative overflow-visible`}
        >
          {/* Play/Pause Button */}
          <button
            onClick={togglePlayPause}
            disabled={state.isDeleting}
            className={`text-indigo-600 flex items-center justify-center mr-4 transition-colors ${
              state.isDeleting
                ? "opacity-50 cursor-not-allowed"
                : "hover:text-indigo-700"
            }`}
            style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            aria-label={state.isPlaying ? "Pause recording" : "Play recording"}
          >
            {state.isPlaying ? (
              <PauseRecording className={responsiveClasses.iconSize} />
            ) : (
              <PlayRecording className={responsiveClasses.iconSize} />
            )}
          </button>

          {/* Progress Bar */}
          <div className="flex-1 relative flex items-center mr-4">
            <div
              ref={progressBarRef}
              className="flex-1 h-1 bg-gray-300 rounded-full relative overflow-visible cursor-pointer"
              onMouseDown={handleProgressBarMouseDown}
            >
              <div
                className="h-1 bg-[#49AFFF] rounded-full"
                style={{
                  width: `${state.progress * 100}%`,
                }}
              />
              <div
                className={`absolute top-1/2 transform -translate-y-1/2 w-4 h-4 bg-[#49AFFF] border-4 border-[#D3E2EB] border-opacity-100 rounded-full shadow-sm ${
                  state.isDragging ? "scale-110" : "hover:scale-110"
                }`}
                style={{
                  left: `${state.progress * 100}%`,
                  marginLeft: "-8px",
                }}
              />
            </div>
          </div>

          {/* Volume Control */}
          <div
            className="flex items-center space-x-2 relative z-50"
            ref={volumeRef}
          >
            <button
              onClick={toggleVolumeSlider}
              className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-gray-800 transition-colors relative z-50"
              aria-label="Volume control"
            >
              {state.isMuted || state.volume === 0 ? (
                <VolumeX className="w-4 h-4" />
              ) : (
                <Volume2 className="w-4 h-4" />
              )}
            </button>

            {/* Volume Slider Popup */}
            {showVolumeSlider && (
              <div className="absolute bottom-full mb-3 -right-2 bg-white border border-gray-200 rounded-lg shadow-xl p-3 min-w-[40px] z-[100]">
                <div className="flex flex-col items-center space-y-2">
                  <div className="text-xs text-gray-600 font-medium">
                    {Math.round(state.volume * 100)}
                  </div>
                  <div
                    ref={volumeSliderRef}
                    className="relative h-20 w-6 flex items-center justify-center cursor-pointer select-none"
                    onMouseDown={handleVolumeSliderMouseDown}
                  >
                    <div className="w-1 h-full bg-gray-300 rounded-full relative">
                      <div
                        className="absolute bottom-0 w-full bg-[#1E1F26] rounded-full transition-all duration-75 ease-out"
                        style={{ height: `${state.volume * 100}%` }}
                      />
                    </div>
                    <div
                      className="absolute w-3 h-3 bg-[#1E1F26] rounded-full shadow-sm pointer-events-none transition-all duration-75 ease-out hover:scale-110"
                      style={{
                        bottom: `${state.volume * 100}%`,
                        left: "50%",
                        transform: "translateX(-50%) translateY(50%)",
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Delete Button */}
        {onDelete && (
          <button
            onClick={handleDelete}
            disabled={state.isDeleting}
            className={`flex items-center justify-center transition-opacity ${
              state.isDeleting
                ? "opacity-50 cursor-not-allowed"
                : "hover:opacity-80"
            }`}
            aria-label="Delete recording"
            style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            title={state.isDeleting ? "Deleting..." : "Delete recording"}
          >
            <DeleteRecording
              className={`${responsiveClasses.buttonSize} text-red-600`}
            />
          </button>
        )}
      </div>
    </div>
  );
};

export default AudioPlayer;
